import { weightedRoll } from "@yjsgames/weighted-roll";
import { ZERO_INSETS, type SafeAreaInsets } from "@yjsgames/safe-area";
import type { ParsedBrickCell } from "./AsciiBrickMapParser.js";
import { GRID, DESIGN } from "../data/balance.js";

/**
 * 챕터 벽돌 그리드의 seed 기반 절차적 블록 패킹 + 픽셀 좌표 변환 (순수 — Phaser 비의존).
 *
 * 모든 챕터를 seed=챕터 번호로 결정론 생성한다: 같은 챕터는 항상 같은 배치.
 * 행 스캔하며 빈 칸에서 1×1/2×1/1×2/2×2 사각형 블록을 가중 추첨(weightedRoll)해 겹침 없이 배치하고,
 * hpMultiplier = 칸 수(widthCells×heightCells)라 큰 블록일수록 HP/보상이 비례 증가한다
 * (절대 HP/골드는 GameScene에서 computeBrickHp/Gold(chapter) × hpMultiplier로 합성).
 *
 * ⚠ 멀티셀 전환의 핵심 설계: 블록 1개 = Brick 오브젝트 1개(셀이 아님). 차지한 span의 중심 좌표
 * (layoutBrickCells)와 width/height만 키워 렌더/히트박스를 확장하므로, 충돌·조준·모듈 효과는
 * 단일 중심 좌표 기준 그대로라 멀티셀 도입에 따른 다운스트림(Projectile/조준/효과 apply) 수정이 없다.
 */

/** 셀당 hpMultiplier(=칸 수) 상한 — 2×2=4. 강도 정규화 SSoT(brickStrength01, 음계 비프용). */
export const CELL_MULTIPLIER_CAP = 4;

/**
 * 챕터 최소 블록 수 — 벽돌깨기 밀도 하한(장르감). density 추첨이 희소하게 나온 seed에서도
 * 빈 칸에 1×1을 채워 보장한다(예산 한도 내). ⚠ balance/global.tsv 이관 후보 — 현재 타 세션
 * 핫존(balance.ts·global.tsv 동시 작업 중)이라 모듈 상수로 보류.
 */
export const MIN_CHAPTER_BLOCKS = 8;

/** 블록 크기 키 → 칸 단위 크기. global.tsv blockWeights 키와 1:1. */
const BLOCK_SIZES: Record<string, { w: number; h: number }> = {
  single: { w: 1, h: 1 },
  horz2: { w: 2, h: 1 },
  vert2: { w: 1, h: 2 },
  square2: { w: 2, h: 2 },
};

export interface SeededGridParams {
  /** 0~1 난수 소스 (makeRng(chapter)). */
  readonly rng: () => number;
  /** 가로 칸 수 (GRID.cols 고정 — 폭 오버플로 방지). */
  readonly cols: number;
  readonly minRows: number;
  readonly maxRows: number;
  /** 빈 칸에서 블록 배치를 시도할 확률 (0~1). */
  readonly density: number;
  /** 총 점유 칸 수 예산. 누적 칸 수가 이를 넘으면 배치 중단. */
  readonly hpMultiplierBudget: number;
  /** 블록 크기 가중치 (BLOCK_SIZES 키 → weight). */
  readonly blockWeights: Record<string, number>;
  /** 최소 블록 수 보장 — density 추첨이 희소한 seed에서 1×1을 채움(예산 한도 내). 미지정 시 보장 없음. */
  readonly minBlocks?: number;
}

export interface SeededGridResult {
  readonly cells: ParsedBrickCell[];
  readonly rows: number;
  readonly cols: number;
}

/** 픽셀 좌표가 붙은 배치 블록. */
export interface PlacedBrickCell extends ParsedBrickCell {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * chapter seed → 결정론 멀티셀 블록. 행 스캔 패킹으로 겹침 없이 사각형 블록을 배치한다.
 * 빈 칸에서 가중 추첨한 블록이 그리드 경계/기존 블록과 충돌하면 1×1로 fallback(현재 칸은 항상 빈칸이라 fit).
 */
export function generateSeededBrickCells(p: SeededGridParams): SeededGridResult {
  const { rng, cols, minRows, maxRows, density, hpMultiplierBudget, blockWeights } = p;
  const rowSpan = Math.max(1, maxRows - minRows + 1);
  const rows = minRows + Math.floor(rng() * rowSpan);

  const occupied: boolean[][] = Array.from({ length: rows }, () => new Array<boolean>(cols).fill(false));
  const blocks: Array<{ col: number; row: number; w: number; h: number }> = [];
  let placedCells = 0;

  const entries = Object.keys(BLOCK_SIZES).map((key) => ({ value: key, weight: blockWeights[key] ?? 0 }));

  const fits = (row: number, col: number, w: number, h: number): boolean => {
    if (col + w > cols || row + h > rows) return false;
    for (let r = row; r < row + h; r++) {
      for (let c = col; c < col + w; c++) if (occupied[r]![c]) return false;
    }
    return true;
  };

  outer: for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (occupied[row]![col]) continue;
      if (rng() >= density) continue;
      if (placedCells >= hpMultiplierBudget) break outer;
      const size = BLOCK_SIZES[weightedRoll(rng, entries, "single")]!;
      let { w, h } = size;
      if (!fits(row, col, w, h)) {
        w = 1;
        h = 1; // 현재 칸은 빈칸이므로 1×1은 항상 fit.
      }
      for (let r = row; r < row + h; r++) {
        for (let c = col; c < col + w; c++) occupied[r]![c] = true;
      }
      blocks.push({ col, row, w, h });
      placedCells += w * h;
    }
  }

  // 밀도 하한 보장 — 블록 수가 minBlocks 미만이면 빈 칸 행 스캔으로 1×1을 채운다(예산 한도 내).
  // 화면이 비어 보이는 희소 seed 방어 (벽돌깨기 장르감). 결정론 유지 — rng 미사용 순차 채움.
  const minBlocks = p.minBlocks ?? 0;
  if (blocks.length < minBlocks) {
    fill: for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (blocks.length >= minBlocks || placedCells >= hpMultiplierBudget) break fill;
        if (occupied[row]![col]) continue;
        occupied[row]![col] = true;
        blocks.push({ col, row, w: 1, h: 1 });
        placedCells += 1;
      }
    }
  }

  // 빈 그리드 방어 (전부 배치 실패 시) — 첫 행을 1×1로 채워 즉시 클리어 폭주를 막는다.
  if (blocks.length === 0) {
    for (let col = 0; col < cols; col++) blocks.push({ col, row: 0, w: 1, h: 1 });
  }

  const cells: ParsedBrickCell[] = blocks
    .map((b) => ({
      col: b.col,
      row: b.row,
      kind: "normal" as const,
      widthCells: b.w,
      heightCells: b.h,
      hpMultiplier: b.w * b.h,
    }))
    .sort((a, b) => a.row - b.row || a.col - b.col);

  return { cells, rows, cols };
}

/**
 * ParsedBrickCell[] → 픽셀 좌표 (가변 cols, 중앙 정렬, fieldTop 기준 상단 정렬).
 * 멀티셀 블록은 좌상단 셀(col,row) 기준으로 폭/높이를 키우고, 차지한 span의 중심으로 원점을 이동한다.
 */
export function layoutBrickCells(
  cells: readonly ParsedBrickCell[],
  cols: number,
  _designWidth?: number, // @deprecated — 내부에서 DESIGN.width로 고정. 이 파라미터는 무시됨.
  insets: SafeAreaInsets = ZERO_INSETS,
  offsetY = 0, // 전투탭 펼치기 시 벽돌을 발사대와 함께 위로 이동(좁혀진 만큼). 마스크가 창 밖을 클립. 0=원복.
): PlacedBrickCell[] {
  const { brickWidth, brickHeight, hGap, vGap, fieldTop } = GRID;
  // 게임뷰가 safe-area로 세로 압축(sY)되므로 벽돌 좌표·크기도 동일 sX/sY로 변환해 게임뷰 interior
  // 마스크 안에 정합시킨다(미변환 시 design 좌표라 압축 마스크 밖으로 클리핑됨). insets=0이면 회귀 0.
  // sX/sY 모두 DESIGN 상수 기준 통일 — portrait-only에서 scale.width === DESIGN.width 보장되나
  // 개념 상 "design 좌표 축소비"는 runtime canvas px가 아닌 DESIGN 상수로 일원화한다.
  const sX = (DESIGN.width - insets.left - insets.right) / DESIGN.width;
  const sY = (DESIGN.height - insets.top - insets.bottom) / DESIGN.height;
  const fieldWidth = cols * brickWidth + (cols - 1) * hGap;
  const startX = (DESIGN.width - fieldWidth) / 2 + brickWidth / 2;
  const colStep = brickWidth + hGap;
  const rowStep = brickHeight + vGap;
  return cells.map((c) => {
    const width = c.widthCells * brickWidth + (c.widthCells - 1) * hGap;
    const height = c.heightCells * brickHeight + (c.heightCells - 1) * vGap;
    const xDesign = startX + c.col * colStep + (width - brickWidth) / 2;
    const yDesign = fieldTop + brickHeight / 2 + c.row * rowStep + (height - brickHeight) / 2;
    return {
      ...c,
      x: insets.left + xDesign * sX,
      y: insets.top + yDesign * sY - offsetY,
      width: width * sX,
      height: height * sY,
    };
  });
}
